import { useEffect, useRef } from 'react';

import { GcVariety } from 'interfaces/IGlobalCatalogue';

import {
  getStepOptionId,
  PICKER_OPTION_ID_ATTRIBUTE,
  PICKER_VARIETY_ID_ATTRIBUTE,
  SEED_TREATMENT_FIELDS_ATTRIBUTE,
  STEP_PICKER_FLYOUT_SELECTOR,
  StepSelectorGroup,
  StepSelectorOption,
} from './stepConfig';

const MULTI_SELECT_PREVIEW_DELAY_MS = 200;

const isFormField = (element: HTMLElement) =>
  element.tagName === 'INPUT' ||
  element.tagName === 'TEXTAREA' ||
  !!element.closest('.ant-select');

const isTextEntryElement = (element: HTMLElement) =>
  element.tagName === 'TEXTAREA' ||
  (element instanceof HTMLInputElement &&
    element.type !== 'checkbox' &&
    element.type !== 'radio');

const isButtonLike = (element: HTMLElement) =>
  element.tagName === 'BUTTON' ||
  element.tagName === 'A' ||
  element.getAttribute('role') === 'button' ||
  (element instanceof HTMLInputElement && element.type === 'checkbox');

const findNavElement = (attribute: string, id: string) =>
  Array.from(document.querySelectorAll<HTMLElement>(`[${attribute}]`)).find(
    (element) => element.getAttribute(attribute) === id
  ) ?? null;

/**
 * Moves DOM focus onto the newly highlighted row so Tab continues from it.
 * - Virtualised catalogue rows may only mount after scrolling, so it retries on the next frame.
 * - Blurs first, so keys never stay with the search box when the row can't be found.
 */
const focusNavElement = (attribute: string, id: string) => {
  const element = findNavElement(attribute, id);
  if (element) {
    element.focus({ preventScroll: true });
    return;
  }
  (document.activeElement as HTMLElement | null)?.blur();
  requestAnimationFrame(() =>
    findNavElement(attribute, id)?.focus({ preventScroll: true })
  );
};

/** Enter on a focused segmented toggle (e.g. /ha | /100kg) moves to the next option. */
const selectNextSegmentedOption = (segmentedEl: HTMLElement) => {
  const inputs = Array.from(
    segmentedEl.querySelectorAll<HTMLInputElement>(
      '.ant-segmented-item-input:not([disabled])'
    )
  );
  if (inputs.length < 2) return;
  const checkedIndex = inputs.findIndex((input) => input.checked);
  const next = inputs[(checkedIndex + 1) % inputs.length];
  next.click();
  next.focus({ focusVisible: true } as FocusOptions);
};

interface UseStepSelectorKeyboardArgs {
  isMultiSelectMode: boolean;
  isMultiSelectLocked: boolean;
  enableMultiSelect: boolean;
  isSeedGroup: boolean;
  /** Add-flow (enableMultiSelect) Seed Treatment tab; the edit two-panel keeps the generic keys. */
  isSeedTreatmentPicker: boolean;
  /** Seed Treatment tab in either flow, including the edit two-panel. */
  isSeedTreatmentTab: boolean;
  /** Tabs other than the active one are locked (lockedCategoryReason), so Left/Right can't leave it. */
  areCategoriesLocked: boolean;
  isCatalogueGroup: boolean;
  highlightedItem: StepSelectorOption | null;
  filteredOptions: StepSelectorOption[];
  groups: StepSelectorGroup[];
  activeCategoryKey: string;
  searchText: string;
  totalCheckedCount: number;
  isSeedFlyoutOpen: boolean;
  pendingSeedVarieties: GcVariety[];
  highlightedSeedVariety: GcVariety | null;
  onSeedVarietyHighlightChange: (variety: GcVariety) => void;
  onConfirmSeedVariety: (event: KeyboardEvent) => void;
  /** Enter on a Seed / Seed Treatment row: same rules as clicking it, with the event's Ctrl/Cmd state. */
  onActivateRow: (item: StepSelectorOption, event: KeyboardEvent) => void;
  onCloseSeedFlyout: () => void;
  onAddSeedTreatment: (event: KeyboardEvent) => void;
  onCloseSeedTreatmentFlyout: () => void;
  onClose: () => void;
  onSave: () => void;
  onCommitSingleSelect: (item: StepSelectorOption) => void;
  onToggleCheckedItem: (item: StepSelectorOption) => void;
  onEnterMultiSelectMode: () => void;
  onExitMultiSelectMode: () => void;
  onHighlightChange: (item: StepSelectorOption) => void;
  onCategoryChange: (group: StepSelectorGroup) => void;
}

/**
 * Keyboard navigation for the step input picker (OP-2231, OP-2323).
 * - Up/Down: move highlight and DOM focus to that row, engage list-nav.
 * - Tab-focusing a row highlights it, so Tab and Up/Down always share one position.
 * - Left/Right: switch tabs once list-nav is engaged; typing (searchText change) disengages.
 * - Escape: close picker (or just the Seed flyout).
 * - Enter: commit / toggle checked; Ctrl/⌘+Enter enters multi-select; Shift+Enter is Save (multi-select),
 *   except inside the open Seed Treatment details, where it confirms them (Add; Save in edit mode).
 * - Seed / Seed Treatment rows: Enter acts like a click (opens the flyout, or checks/unchecks once
 *   multi-select is locked); Ctrl/⌘+Enter acts like a Ctrl/⌘+click.
 * - Seed flyout: Up/Down/Enter pick a variety (Ctrl/⌘+Enter stages it), Escape/Left close it.
 *   Its highlight is only a cursor (like mouse hover): Shift+Enter saves without picking it.
 * - Seed Treatment flyout: Enter adds (Ctrl/⌘+Enter stages), Escape/Left close it.
 * - Seed Treatment flyout or edit two-panel open: Up/Down leave the product highlight alone.
 * - Edit mode (two-panels): Enter on a row opens it, Enter in the Seed Treatment panel saves,
 *   and Escape closes the whole picker; Left/Right never leave the Seed Treatment panel.
 * - Locked tabs (lockedCategoryReason): Left/Right never switch tabs.
 * - Arrow keys are ignored on a focused toggle (/ha | /100kg); only Enter switches it.
 * - In a text field Left/Right always move its cursor (no flyout close, no tab switch).
 * - Inside a flyout: Escape first blurs a focused field (focus moves to the flyout box).
 * - Enter on a Tab-focused button / checkbox clicks it (with Ctrl/⌘), instead of the highlighted row;
 *   with a flyout open, Enter on a main-list row does the flyout action instead.
 * - Unchecking is Enter / click on a checked row; there is no Delete/Backspace shortcut.
 * - antd Select/InputNumber own their keys; Escape passes through unless a Select dropdown is open.
 */
export const useStepSelectorKeyboard = (
  args: UseStepSelectorKeyboardArgs
): void => {
  // Listeners subscribe once and read the latest render's values, so they never act on stale props.
  const argsRef = useRef(args);
  argsRef.current = args;
  const hasEngagedListNavRef = useRef(false);

  useEffect(() => {
    hasEngagedListNavRef.current = false;
  }, [args.searchText]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const {
        isMultiSelectMode,
        enableMultiSelect,
        isSeedGroup,
        isSeedTreatmentPicker,
        isSeedTreatmentTab,
        areCategoriesLocked,
        isCatalogueGroup,
        highlightedItem,
        filteredOptions,
        groups,
        activeCategoryKey,
        isSeedFlyoutOpen,
        pendingSeedVarieties,
        highlightedSeedVariety,
        onSeedVarietyHighlightChange,
        onConfirmSeedVariety,
        onActivateRow,
        onCloseSeedFlyout,
        onAddSeedTreatment,
        onCloseSeedTreatmentFlyout,
        onClose,
        onSave,
        onCommitSingleSelect,
        onToggleCheckedItem,
        onEnterMultiSelectMode,
        onHighlightChange,
        onCategoryChange,
      } = argsRef.current;
      const isSeedTreatmentFlyoutOpen =
        isSeedTreatmentPicker && isSeedFlyoutOpen;
      const target = event.target instanceof HTMLElement ? event.target : null;
      const flyoutEl =
        target?.closest<HTMLElement>(STEP_PICKER_FLYOUT_SELECTOR) ?? null;

      // Shift+Enter inside the open Seed Treatment details confirms them (Add, or Save in edit mode).
      const isInSeedTreatmentDetails =
        isSeedTreatmentTab &&
        isSeedFlyoutOpen &&
        (!!flyoutEl ||
          !!target?.closest(`[${SEED_TREATMENT_FIELDS_ATTRIBUTE}]`));
      if (
        event.key === 'Enter' &&
        event.shiftKey &&
        isInSeedTreatmentDetails &&
        !target?.closest('.ant-select-open')
      ) {
        event.preventDefault();
        event.stopPropagation();
        onAddSeedTreatment(event);
        return;
      }

      const antFieldEl = (event.target as HTMLElement | null)?.closest?.(
        '.ant-select, .ant-input-number'
      );
      if (
        antFieldEl &&
        (event.key !== 'Escape' ||
          antFieldEl.classList.contains('ant-select-open'))
      ) {
        return;
      }

      // Toggles (/ha | /100kg) change with Enter only, so arrows neither switch them nor reach the picker.
      if (event.key.startsWith('Arrow') && target?.closest('.ant-segmented')) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        if (target && flyoutEl && target !== flyoutEl && isFormField(target)) {
          event.stopPropagation();
          flyoutEl.focus();
          return;
        }
        // Add flow only: Escape backs out of the variety flyout; in edit mode it closes the picker.
        if (isSeedGroup && isSeedFlyoutOpen && enableMultiSelect) {
          event.stopPropagation();
          onCloseSeedFlyout();
          return;
        }
        if (isSeedTreatmentFlyoutOpen) {
          event.stopPropagation();
          onCloseSeedTreatmentFlyout();
          return;
        }
        onClose();
        return;
      }

      if (event.key === 'Enter') {
        const wantsMultiSelect = event.ctrlKey || event.metaKey;

        const segmentedEl = target?.closest<HTMLElement>('.ant-segmented');
        if (segmentedEl) {
          event.preventDefault();
          event.stopPropagation();
          selectNextSegmentedOption(segmentedEl);
          return;
        }
        // With a flyout open, Enter on a main-list row belongs to the flyout (Add / confirm variety).
        const isListRowWithFlyoutOpen =
          isSeedFlyoutOpen &&
          !flyoutEl &&
          !!target?.closest(`[${PICKER_OPTION_ID_ATTRIBUTE}]`);
        // Mid multi-select, Ctrl/⌘+Enter and Shift+Enter both confirm the
        // selection and close the picker (OP-2534) — even from a focused
        // control, so neither ever just toggles another row.
        const wantsConfirm = event.shiftKey || wantsMultiSelect;
        // A Tab-focused control gets the click, carrying Ctrl/Cmd so it follows the click rules.
        if (
          target &&
          isButtonLike(target) &&
          !event.shiftKey &&
          !(isMultiSelectMode && wantsMultiSelect) &&
          !isListRowWithFlyoutOpen
        ) {
          event.preventDefault();
          target.dispatchEvent(
            new MouseEvent('click', {
              bubbles: true,
              cancelable: true,
              ctrlKey: event.ctrlKey,
              metaKey: event.metaKey,
            })
          );
          return;
        }

        // Save, even with a flyout open (Save itself decides if it can run).
        if (isMultiSelectMode && wantsConfirm) {
          event.preventDefault();
          onSave();
          return;
        }

        if (isSeedGroup && isSeedFlyoutOpen) {
          event.preventDefault();
          if (highlightedSeedVariety) onConfirmSeedVariety(event);
          return;
        }

        // Add-flow flyout and edit panel alike: Enter adds / saves the current draft.
        if (isSeedTreatmentTab && isSeedFlyoutOpen) {
          event.preventDefault();
          onAddSeedTreatment(event);
          return;
        }

        if (isSeedGroup || isSeedTreatmentTab) {
          if (highlightedItem) {
            event.preventDefault();
            onActivateRow(highlightedItem, event);
          }
          return;
        }

        if (isMultiSelectMode) {
          if (highlightedItem) {
            event.preventDefault();
            onToggleCheckedItem(highlightedItem);
          }
          return;
        }

        if (enableMultiSelect && wantsMultiSelect && highlightedItem) {
          event.preventDefault();
          onEnterMultiSelectMode();
          onToggleCheckedItem(highlightedItem);
          return;
        }

        if (highlightedItem) {
          event.preventDefault();
          onCommitSingleSelect(highlightedItem);
        }
        return;
      }

      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        if (target && isTextEntryElement(target)) return;
        if (isSeedTreatmentFlyoutOpen) {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            onCloseSeedTreatmentFlyout();
          }
          return;
        }
        if (isSeedGroup && isSeedFlyoutOpen) {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            onCloseSeedFlyout();
          }

          return;
        }
        // The Seed Treatment edit panel holds an unsaved draft, so Left/Right don't switch tabs.
        if (isSeedTreatmentTab && isSeedFlyoutOpen) return;
        if (areCategoriesLocked) return;
        if (!hasEngagedListNavRef.current) return;

        const currentIndex = groups.findIndex(
          (group) => group.key === activeCategoryKey
        );
        if (currentIndex === -1) return;

        const nextIndex =
          event.key === 'ArrowRight'
            ? Math.min(currentIndex + 1, groups.length - 1)
            : Math.max(currentIndex - 1, 0);
        if (nextIndex === currentIndex) return;

        event.preventDefault();
        onCategoryChange(groups[nextIndex]);
        return;
      }

      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      // An open Seed Treatment flyout or edit panel shows the highlighted product, so it stays put.
      if (isSeedTreatmentTab && isSeedFlyoutOpen) return;

      if (isSeedGroup && isSeedFlyoutOpen) {
        if (pendingSeedVarieties.length === 0) return;
        event.preventDefault();

        const currentVarietyIndex = highlightedSeedVariety
          ? pendingSeedVarieties.findIndex(
              (variety) =>
                variety.varietyId === highlightedSeedVariety.varietyId
            )
          : -1;
        const nextVarietyIndex =
          event.key === 'ArrowDown'
            ? Math.min(currentVarietyIndex + 1, pendingSeedVarieties.length - 1)
            : Math.max(currentVarietyIndex - 1, 0);

        const nextVariety = pendingSeedVarieties[nextVarietyIndex];
        onSeedVarietyHighlightChange(nextVariety);
        focusNavElement(PICKER_VARIETY_ID_ATTRIBUTE, nextVariety.varietyId);
        return;
      }

      if (filteredOptions.length === 0) return;
      event.preventDefault();
      hasEngagedListNavRef.current = true;

      const currentIndex = highlightedItem
        ? filteredOptions.findIndex((option) =>
            isCatalogueGroup
              ? option.globalId === highlightedItem.globalId
              : option.item === highlightedItem.item
          )
        : -1;
      const nextIndex =
        event.key === 'ArrowDown'
          ? Math.min(currentIndex + 1, filteredOptions.length - 1)
          : Math.max(currentIndex - 1, 0);

      const nextOption = filteredOptions[nextIndex];
      onHighlightChange(nextOption);
      focusNavElement(PICKER_OPTION_ID_ATTRIBUTE, getStepOptionId(nextOption));
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, []);

  useEffect(() => {
    const handleFocusIn = (event: FocusEvent) => {
      const {
        filteredOptions,
        isSeedFlyoutOpen,
        pendingSeedVarieties,
        onSeedVarietyHighlightChange,
        onHighlightChange,
      } = argsRef.current;
      if (!(event.target instanceof HTMLElement)) return;
      // Back in a text field: Left/Right move the cursor again instead of switching tabs.
      if (isTextEntryElement(event.target)) {
        hasEngagedListNavRef.current = false;
        return;
      }

      const varietyId = event.target
        .closest(`[${PICKER_VARIETY_ID_ATTRIBUTE}]`)
        ?.getAttribute(PICKER_VARIETY_ID_ATTRIBUTE);
      if (varietyId) {
        const variety = pendingSeedVarieties.find(
          (candidate) => candidate.varietyId === varietyId
        );
        if (variety) onSeedVarietyHighlightChange(variety);
        return;
      }

      // With a flyout open the highlight is its row, so focusing another row must not swap it.
      if (isSeedFlyoutOpen) return;
      const optionId = event.target
        .closest(`[${PICKER_OPTION_ID_ATTRIBUTE}]`)
        ?.getAttribute(PICKER_OPTION_ID_ATTRIBUTE);
      if (!optionId) return;
      const option = filteredOptions.find(
        (candidate) => getStepOptionId(candidate) === optionId
      );
      if (!option) return;
      hasEngagedListNavRef.current = true;
      onHighlightChange(option);
    };

    document.addEventListener('focusin', handleFocusIn);
    return () => document.removeEventListener('focusin', handleFocusIn);
  }, []);

  useEffect(() => {
    let pendingEnterTimeout: ReturnType<typeof setTimeout> | null = null;

    const isMultiSelectKey = (event: KeyboardEvent) =>
      event.key === 'Control' || event.key === 'Meta';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isMultiSelectKey(event)) return;
      const { enableMultiSelect, isMultiSelectMode } = argsRef.current;
      if (!enableMultiSelect || isMultiSelectMode) return;
      if (pendingEnterTimeout) return;

      pendingEnterTimeout = setTimeout(() => {
        argsRef.current.onEnterMultiSelectMode();
        pendingEnterTimeout = null;
      }, MULTI_SELECT_PREVIEW_DELAY_MS);
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (!isMultiSelectKey(event)) return;
      const {
        isMultiSelectMode,
        isMultiSelectLocked,
        totalCheckedCount,
        onExitMultiSelectMode,
      } = argsRef.current;

      if (pendingEnterTimeout) {
        clearTimeout(pendingEnterTimeout);
        pendingEnterTimeout = null;
        return;
      }

      // A preview (held without a locking Ctrl/Cmd+click) reverts on release, even with a flyout open.
      if (
        isMultiSelectMode &&
        !isMultiSelectLocked &&
        totalCheckedCount === 0
      ) {
        onExitMultiSelectMode();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
      if (pendingEnterTimeout) {
        clearTimeout(pendingEnterTimeout);
      }
    };
  }, []);
};
